import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import StaffHeader from '../components/StaffHeader';
import usePullToRefresh from '../hooks/usePullToRefresh';
import usePagination from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import PhotoGalleryModal from '../components/PhotoGalleryModal';
import { PRODUCT_TYPE } from '../config/product';
import { formatCurrency } from '../utils/formatCurrency';
import { companyToday } from '../utils/companyDate';
import { AWAITING_DEPTH_LABEL, snowServiceLabel, premiumLineLabel } from '../utils/stopPrice';
import usePaymentMethods from '../hooks/usePaymentMethods';
import { paymentStatusLabel, markPaidOptions, markPaidDefault } from '../utils/paymentMethods';

// An unpriced stop (lock NULL) is a dash, not $0.00.
const formatPrice = (price) => (price === null || price === undefined ? '—' : formatCurrency(price));

// A stop awaiting its storm depth has no figure: "Awaiting depth", never a
// dash and never $0.00.
const formatServicePrice = (service) =>
  (service.price_pending_depth === true ? AWAITING_DEPTH_LABEL : formatPrice(service.price));

// The band and the on-call premium a depth-priced stop was charged at, as
// lines under the address: "Snow removal, 6.1-9 in" and "On-call premium (20%)".
const DepthLines = ({ service }) => {
  if (service.price_pending_depth === true) return null;
  const band = service.depth_band_label;
  const pct = parseFloat(service.on_call_premium_percent);
  if (!band && !(pct > 0)) return null;
  return (
    <>
      {band && <div className="service-depth-line">{snowServiceLabel(band)}</div>}
      {pct > 0 && <div className="service-depth-line">{premiumLineLabel(pct)}</div>}
    </>
  );
};

function ServiceHistory() {
  const { isAdmin, shouldHidePrices, timezone } = useAuth();

  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoCountsMap, setPhotoCountsMap] = useState({});
  const [photoGalleryStopId, setPhotoGalleryStopId] = useState(null);
  const [paymentModal, setPaymentModal] = useState({ open: false, service: null });
  const paymentMethods = usePaymentMethods();
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: 'cash',
    paymentDate: companyToday(timezone),
    paymentNotes: ''
  });
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: 'info' });

  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'info' }), 5000);
  };

  // Filters
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    customerId: '',
    routeId: '',
    paymentStatus: '',
  });

  // quiet: a pull-to-refresh keeps the list on screen instead of the spinner.
  const loadData = useCallback(async ({ quiet = false } = {}) => {
    try {
      if (!quiet) setLoading(true);

      // Build query params
      const params = new URLSearchParams();
      params.append('product', PRODUCT_TYPE);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.customerId) params.append('customerId', filters.customerId);
      if (filters.routeId) params.append('routeId', filters.routeId);
      if (filters.paymentStatus) params.append('paymentStatus', filters.paymentStatus);

      const [servicesRes, customersRes, routesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/services?${params.toString()}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/customers?product=${PRODUCT_TYPE}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/routes?product=${PRODUCT_TYPE}`),
      ]);

      setServices(servicesRes.data);
      setCustomers(customersRes.data);
      setRoutes(routesRes.data);

      // Fetch photo counts for services that have route_stop_id
      const stopIds = servicesRes.data
        .filter(s => s.route_stop_id)
        .map(s => s.route_stop_id);
      if (stopIds.length > 0) {
        try {
          const countsRes = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/route-stops/photo-counts`,
            { stopIds }
          );
          setPhotoCountsMap(countsRes.data);
        } catch (photoErr) {
          console.error('Failed to load photo counts:', photoErr);
        }
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const refreshHistory = useCallback(() => loadData({ quiet: true }), [loadData]);
  const { PullToRefreshIndicator } = usePullToRefresh(refreshHistory);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      customerId: '',
      routeId: '',
      paymentStatus: '',
    });
  };

  // Pagination for services list
  const {
    paginatedData: paginatedServices,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalItems
  } = usePagination(services, 'serviceHistory', 25);

  // Note: service_date is pre-formatted by backend as "Mon DD, YYYY"
  // so we display it directly without Date parsing

  const formatPaymentStatus = (status, method) => paymentStatusLabel(paymentMethods, status, method);

  const getPaymentBadgeClass = (status) => {
    if (status === 'paid') return 'paid';
    if (status === 'invoice_sent') return 'invoice-sent';
    return 'not-paid';
  };

  // Open payment modal for a service
  const openPaymentModal = (service) => {
    setPaymentForm({
      paymentMethod: markPaidDefault(paymentMethods, service.payment_method),
      paymentDate: companyToday(timezone),
      paymentNotes: '',
      amountCollected: ''
    });
    setPaymentError('');
    setPaymentModal({ open: true, service });
  };

  // Submit payment update
  const submitPayment = async () => {
    if (!paymentModal.service) return;

    // A stop awaiting the storm depth has no price (design 3.4): paid at the
    // door it needs the amount collected; Square records "invoice after
    // depth" (not paid, no amount), which Bill All picks up later.
    const pendingDepth = paymentModal.service.price_pending_depth === true;
    const invoiceAfterDepth = pendingDepth && paymentForm.paymentMethod === 'square';
    const amount = parseFloat(paymentForm.amountCollected);
    if (pendingDepth && !invoiceAfterDepth && !(amount > 0)) {
      setPaymentError('This stop is waiting for the storm depth, so enter the amount you collected.');
      return;
    }
    setPaymentError('');

    setSavingPayment(true);
    try {
      // Use route-stops endpoint if route_stop_id exists, otherwise use services endpoint
      const endpoint = paymentModal.service.route_stop_id
        ? `${process.env.REACT_APP_API_URL}/api/route-stops/${paymentModal.service.route_stop_id}/payment`
        : `${process.env.REACT_APP_API_URL}/api/services/${paymentModal.service.id}/payment`;

      await axios.put(endpoint, {
        paymentStatus: invoiceAfterDepth ? 'not_paid' : 'paid',
        paymentMethod: paymentForm.paymentMethod,
        paymentDate: invoiceAfterDepth ? undefined : paymentForm.paymentDate,
        paymentNotes: paymentForm.paymentNotes || undefined,
        amountPaid: pendingDepth && !invoiceAfterDepth ? amount : undefined
      });
      setPaymentModal({ open: false, service: null });
      loadData();
    } catch (err) {
      console.error('Failed to update payment:', err);
      showNotification(err.response?.data?.error || 'Failed to update payment status', 'error');
    } finally {
      setSavingPayment(false);
    }
  };

  // Calculate totals
  const totalServices = services.length;
  const totalRevenue = services.reduce((sum, s) => sum + parseFloat(s.price || 0), 0);
  // Stops awaiting depth are left out of the revenue figure and counted.
  const pendingDepthServices = services.filter(s => s.price_pending_depth === true).length;
  const paidCount = services.filter(s => s.payment_status === 'paid').length;

  return (
    <div className="service-history-page">
      {notification.show && (
        <div className={`notification-toast notification-${notification.type}`} role="alert">
          <span>{notification.message}</span>
          <button onClick={() => setNotification({ show: false, message: '', type: 'info' })} aria-label="Dismiss">&times;</button>
        </div>
      )}
      {/* Header */}
      <StaffHeader active="history" />

      <PullToRefreshIndicator />

      <div className="page-container">
        {/* Page Header */}
        <div className="page-header">
          <h1 className="page-title">Service History</h1>
          <p className="text-muted">{totalServices} services completed</p>
        </div>

        {/* Summary Stats */}
        <div className="history-stats">
          <div className="history-stat">
            <span className="stat-value">{totalServices}</span>
            <span className="stat-label">Services</span>
          </div>
          {!shouldHidePrices() && (
            <>
              <div className="history-stat">
                <span className="stat-value">
                  {pendingDepthServices > 0 && totalServices === pendingDepthServices
                    ? AWAITING_DEPTH_LABEL
                    : formatCurrency(totalRevenue)}
                </span>
                <span className="stat-label">
                  Revenue{pendingDepthServices > 0 && totalServices !== pendingDepthServices ? ` + ${pendingDepthServices} awaiting depth` : ''}
                </span>
              </div>
              <div className="history-stat">
                <span className="stat-value">{paidCount}</span>
                <span className="stat-label">Paid</span>
              </div>
              <div className="history-stat">
                <span className="stat-value">{totalServices - paidCount}</span>
                <span className="stat-label">Unpaid</span>
              </div>
            </>
          )}
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="filters-grid">
            <div className="filter-group">
              <label>From Date</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="form-input"
              />
            </div>
            <div className="filter-group">
              <label>To Date</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="form-input"
              />
            </div>
            <div className="filter-group">
              <label>Customer</label>
              <select
                name="customerId"
                value={filters.customerId}
                onChange={handleFilterChange}
                className="form-input"
              >
                <option value="">All Customers</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.first_name} {c.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Route</label>
              <select
                name="routeId"
                value={filters.routeId}
                onChange={handleFilterChange}
                className="form-input"
              >
                <option value="">All Routes</option>
                {routes.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Payment</label>
              <select
                name="paymentStatus"
                value={filters.paymentStatus}
                onChange={handleFilterChange}
                className="form-input"
              >
                <option value="">All</option>
                <option value="paid">Paid</option>
                <option value="not_paid">Not Paid</option>
              </select>
            </div>
            <div className="filter-group filter-actions">
              <button className="btn btn-outline btn-sm" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Services List */}
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : services.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: '3rem', marginBottom: '12px', opacity: 0.5 }}>🕐</div>
            <h3 style={{ margin: '0 0 8px', color: 'var(--color-text)' }}>No service history yet</h3>
            <p style={{ color: 'var(--color-text-light)', margin: '0 0 16px' }}>Complete your first route to see service history here</p>
            {(filters.startDate || filters.endDate || filters.customerId || filters.routeId || filters.paymentStatus) && (
              <button className="btn btn-outline" onClick={clearFilters}>
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="services-table-container desktop-only">
              <table className="services-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Address</th>
                    <th>Route</th>
                    {!shouldHidePrices() && <th className="text-right">Price</th>}
                    {!shouldHidePrices() && <th>Payment</th>}
                  </tr>
                </thead>
                <tbody>
                  {paginatedServices.map(service => (
                    <tr key={service.id}>
                      <td>{service.service_date}</td>
                      <td className="customer-cell">
                        {service.first_name} {service.last_name}
                      </td>
                      <td className="address-cell">
                        {service.address}
                        {service.city && <span className="city-text">, {service.city}</span>}
                        <DepthLines service={service} />
                      </td>
                      <td>{service.route_name || '-'}</td>
                      {!shouldHidePrices() && (
                        <td className={`text-right price-cell ${service.payment_status === 'paid' || service.is_comp ? 'paid' : 'unpaid'}`}>
                          {formatServicePrice(service)}
                        </td>
                      )}
                      {!shouldHidePrices() && (
                        <td>
                          <div className="payment-cell">
                            <span className={`payment-badge ${getPaymentBadgeClass(service.payment_status)}`}>
                              {formatPaymentStatus(service.payment_status, service.payment_method)}
                            </span>
                            {service.payment_status !== 'paid' && !service.is_comp && (
                              <button
                                className="btn-mark-paid"
                                onClick={() => openPaymentModal(service)}
                              >
                                Mark Paid
                              </button>
                            )}
                            {service.is_comp && (
                              <span className="comp-badge" title={service.comp_reason || 'Comp/Free'}>
                                COMP
                              </span>
                            )}
                            {photoCountsMap[service.route_stop_id] > 0 && (
                              <span
                                className="photo-count-badge clickable"
                                title={`${photoCountsMap[service.route_stop_id]} photo${photoCountsMap[service.route_stop_id] > 1 ? 's' : ''} - Click to view`}
                                onClick={() => setPhotoGalleryStopId(service.route_stop_id)}
                              >
                                📷 {photoCountsMap[service.route_stop_id]}
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="services-cards mobile-only">
              {paginatedServices.map(service => (
                <div key={service.id} className="service-card">
                  <div className="service-card-header">
                    <span className="service-date">{service.service_date}</span>
                    {!shouldHidePrices() && (
                      <span className={`payment-badge ${getPaymentBadgeClass(service.payment_status)}`}>
                        {formatPaymentStatus(service.payment_status, service.payment_method)}
                      </span>
                    )}
                    {service.is_comp && (
                      <span className="comp-badge">COMP</span>
                    )}
                    {photoCountsMap[service.route_stop_id] > 0 && (
                      <span
                        className="photo-count-badge clickable"
                        onClick={() => setPhotoGalleryStopId(service.route_stop_id)}
                      >
                        📷 {photoCountsMap[service.route_stop_id]}
                      </span>
                    )}
                  </div>
                  <div className="service-card-body">
                    <div className="service-customer">
                      {service.first_name} {service.last_name}
                    </div>
                    <div className="service-address">
                      {service.address}{service.city && `, ${service.city}`}
                    </div>
                    {service.route_name && (
                      <div className="service-route">Route: {service.route_name}</div>
                    )}
                    <DepthLines service={service} />
                  </div>
                  <div className="service-card-footer">
                    {!shouldHidePrices() && (
                      <span className={`service-price ${service.payment_status === 'paid' || service.is_comp ? 'paid' : 'unpaid'}`}>{formatServicePrice(service)}</span>
                    )}
                    {!shouldHidePrices() && service.payment_status !== 'paid' && !service.is_comp && (
                      <button
                        className="btn-mark-paid"
                        onClick={() => openPaymentModal(service)}
                      >
                        Mark Paid
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </>
        )}
      </div>

      {/* Payment Modal */}
      {paymentModal.open && paymentModal.service && (
        <div className="payment-modal-overlay" onClick={() => setPaymentModal({ open: false, service: null })}>
          <div className="payment-modal" onClick={e => e.stopPropagation()}>
            <div className="payment-modal-header">
              <h3>Mark as Paid</h3>
              <button className="modal-close" onClick={() => setPaymentModal({ open: false, service: null })} aria-label="Close">&times;</button>
            </div>
            <div className="payment-modal-body">
              <p className="payment-customer">
                <strong>{paymentModal.service.first_name} {paymentModal.service.last_name}</strong>
                <br />
                <span className="payment-service-date">{paymentModal.service.service_date}</span>
                {!shouldHidePrices() && (
                  <span className="payment-amount"> - {formatServicePrice(paymentModal.service)}</span>
                )}
              </p>

              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={paymentForm.paymentMethod}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  {markPaidOptions(paymentMethods, paymentModal.service.payment_method).map(option => (
                    <option key={option.id} value={option.id}>
                      {option.id === 'square' && paymentModal.service.price_pending_depth === true ? 'Square, invoice after depth' : option.name}
                    </option>
                  ))}
                </select>
              </div>

              {paymentModal.service.price_pending_depth === true && paymentForm.paymentMethod === 'square' && (
                <p className="text-muted">Recorded as Square, not paid. The invoice goes out after the storm depth is entered.</p>
              )}
              {paymentModal.service.price_pending_depth === true && paymentForm.paymentMethod !== 'square' && (
                <div className="form-group">
                  <label>Amount collected *</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0.01"
                    value={paymentForm.amountCollected}
                    onChange={e => setPaymentForm(prev => ({ ...prev, amountCollected: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
              )}
              {paymentError && <div className="alert alert-error" role="alert">{paymentError}</div>}

              <div className="form-group">
                <label>Payment Date</label>
                <input
                  type="date"
                  value={paymentForm.paymentDate}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label>Notes (optional)</label>
                <input
                  type="text"
                  value={paymentForm.paymentNotes}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paymentNotes: e.target.value }))}
                  placeholder="e.g., Check #1234"
                />
              </div>
            </div>
            <div className="payment-modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setPaymentModal({ open: false, service: null })}
              >
                Cancel
              </button>
              <button
                className="btn-confirm"
                onClick={submitPayment}
                disabled={savingPayment}
              >
                {savingPayment ? 'Saving...' : 'Mark Paid'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-link {
          background: var(--color-secondary) !important;
          color: white !important;
        }
        .storm-link:hover {
          background: var(--color-secondary-dark) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }

        /* Mobile Menu */
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-link {
          background: var(--color-secondary);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }

        /* Summary Stats */
        .history-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }
        .history-stat {
          background: var(--color-card);
          padding: 16px;
          border-radius: 10px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .history-stat .stat-value {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-primary);
        }
        .history-stat .stat-label {
          font-size: 0.75rem;
          color: var(--color-text-light);
          text-transform: uppercase;
        }

        /* Filters */
        .filters-section {
          background: var(--color-card);
          padding: 16px;
          border-radius: 12px;
          margin-bottom: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .filters-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 12px;
          align-items: end;
        }
        .filter-group label {
          display: block;
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-text-light);
          margin-bottom: 4px;
          text-transform: uppercase;
        }
        .filter-group .form-input {
          width: 100%;
          font-size: 0.875rem;
        }
        .filter-actions {
          display: flex;
          align-items: flex-end;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 48px 16px;
          color: var(--color-text-light);
        }
        .empty-state .btn {
          margin-top: 16px;
        }

        /* Services Table (Desktop) */
        .services-table-container {
          background: var(--color-card);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .services-table {
          width: 100%;
          border-collapse: collapse;
        }
        .services-table th,
        .services-table td {
          padding: 12px 16px;
          text-align: left;
          border-bottom: 1px solid var(--color-border);
        }
        .services-table th {
          background: var(--color-background);
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          color: var(--color-text-light);
        }
        .services-table tbody tr:hover {
          background: var(--color-background);
        }
        .services-table tbody tr:last-child td {
          border-bottom: none;
        }
        .customer-cell {
          font-weight: 500;
        }
        .address-cell {
          color: var(--color-text-light);
          font-size: 0.875rem;
        }
        .city-text {
          color: var(--color-text-light);
        }
        .service-depth-line {
          font-size: 0.8rem;
          color: var(--color-text-light);
        }

        .price-cell {
          font-weight: 600;
        }
        .price-cell.paid {
          color: #16a34a;
        }
        .price-cell.unpaid {
          color: #dc2626;
        }
        .text-right {
          text-align: right;
        }

        /* Payment Badge */
        .payment-cell {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .payment-badge {
          display: inline-block;
          font-size: 0.7rem;
          padding: 4px 8px;
          border-radius: 10px;
          font-weight: 600;
          white-space: nowrap;
        }
        .payment-badge.paid {
          background: rgba(34, 197, 94, 0.15);
          color: #15803d;
        }
        .payment-badge.invoice-sent {
          background: rgba(234, 179, 8, 0.15);
          color: #a16207;
        }
        .payment-badge.not-paid {
          background: rgba(156, 163, 175, 0.15);
          color: #6b7280;
        }
        .btn-mark-paid {
          padding: 4px 10px;
          font-size: 0.7rem;
          background: #22c55e;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          white-space: nowrap;
        }
        .btn-mark-paid:hover {
          background: #16a34a;
        }

        /* Payment Modal */
        .payment-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 200;
          padding: 20px;
        }
        .payment-modal {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 400px;
          overflow: hidden;
        }
        .payment-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #e2e8f0;
        }
        .payment-modal-header h3 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #64748b;
          padding: 0;
          line-height: 1;
        }
        .payment-modal-body {
          padding: 20px;
        }
        .payment-customer {
          margin: 0 0 16px 0;
          font-size: 0.95rem;
        }
        .payment-service-date {
          color: #64748b;
        }
        .payment-amount {
          color: #059669;
          font-weight: 600;
        }
        .payment-modal .form-group {
          margin-bottom: 16px;
        }
        .payment-modal .form-group label {
          display: block;
          font-size: 0.85rem;
          font-weight: 500;
          margin-bottom: 6px;
          color: #374151;
        }
        .payment-modal .form-group select,
        .payment-modal .form-group input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.95rem;
        }
        .payment-modal-footer {
          display: flex;
          gap: 12px;
          padding: 16px 20px;
          border-top: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        .payment-modal-footer .btn-cancel {
          flex: 1;
          padding: 10px;
          border: 1px solid #d1d5db;
          background: white;
          border-radius: 8px;
          font-size: 0.95rem;
          cursor: pointer;
        }
        .payment-modal-footer .btn-confirm {
          flex: 1;
          padding: 10px;
          border: none;
          background: #22c55e;
          color: white;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
        }
        .payment-modal-footer .btn-confirm:hover {
          background: #16a34a;
        }
        .payment-modal-footer .btn-confirm:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Photo Count Badge */
        .photo-count-badge {
          font-size: 0.7rem;
          padding: 3px 8px;
          border-radius: 10px;
          font-weight: 500;
          white-space: nowrap;
          background: rgba(59, 130, 246, 0.15);
          color: #2563eb;
        }
        .photo-count-badge.clickable {
          cursor: pointer;
          transition: background 0.15s, transform 0.1s;
        }
        .photo-count-badge.clickable:hover {
          background: rgba(59, 130, 246, 0.25);
          transform: scale(1.05);
        }

        /* Mobile Cards */
        .services-cards {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .service-card {
          background: var(--color-card);
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .service-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        .service-date {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }
        .service-card-body {
          margin-bottom: 12px;
        }
        .service-customer {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 4px;
        }
        .service-address {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }
        .service-route {
          font-size: 0.75rem;
          color: var(--color-text-light);
          margin-top: 8px;
        }
        .service-card-footer {
          display: flex;
          justify-content: flex-end;
          padding-top: 12px;
          border-top: 1px solid var(--color-border);
        }
        .service-price {
          font-size: 1.25rem;
          font-weight: 700;
        }
        .service-price.paid {
          color: #16a34a;
        }
        .service-price.unpaid {
          color: #dc2626;
        }

        /* Responsive */
        .desktop-only {
          display: block;
        }
        .mobile-only {
          display: none;
        }

        @media (max-width: 900px) {
          .desktop-nav {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .history-stats {
            grid-template-columns: repeat(2, 1fr);
          }
          .filters-grid {
            grid-template-columns: 1fr 1fr;
          }
          .filter-actions {
            grid-column: span 2;
            justify-content: center;
          }
          .desktop-only {
            display: none;
          }
          .mobile-only {
            display: block;
          }
        }

        @media (max-width: 480px) {
          .filters-grid {
            grid-template-columns: 1fr;
          }
          .filter-actions {
            grid-column: span 1;
          }
        }
      `}</style>

      {/* Photo Gallery Modal */}
      <PhotoGalleryModal
        stopId={photoGalleryStopId}
        isOpen={photoGalleryStopId !== null}
        onClose={() => setPhotoGalleryStopId(null)}
        allowDelete={isAdmin}
        title="Service Photos"
      />
    </div>
  );
}

export default ServiceHistory;

